/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import axios from "axios";
import client, { ApiError } from "./client";

/* -------------------------------------------------------------------------- */
/* Storage-provider boundary                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The API currently names its presign fields after S3 (`s3_bucket`, `s3_key`).
 * Those are deliberately dropped here: the frontend treats the signed URL as an
 * opaque string, so moving a bucket to Azure Blob or GCS is a server-only change
 * and no component needs touching.
 *
 * `method` and `headers` are read from the response when present and otherwise
 * default to the S3 shape. Azure Blob SAS uploads require
 * `x-ms-blob-type: BlockBlob`; when that backend lands the API can start
 * returning those fields and this client already honours them.
 */
const normalizeUploadTarget = (data) => ({
  fileId: data.file_id,
  uploadUrl: data.upload_url,
  method: (data.upload_method || "PUT").toUpperCase(),
  headers: data.upload_headers || null,
  expiresInSeconds: data.expires_in_seconds,
});

const normalizeDownloadTarget = (data) => ({
  fileId: data.file_id,
  downloadUrl: data.download_url,
  expiresInSeconds: data.expires_in_seconds,
});

/* -------------------------------------------------------------------------- */
/* Folders                                                                     */
/* -------------------------------------------------------------------------- */

export const listFolders = async ({ parentFolderId = null, folderName, pageNumber = 1, pageSize = 100 } = {}) => {
  const { data } = await client.get("/drive/folders", {
    params: {
      parent_folder_id: parentFolderId ?? undefined,
      folder_name: folderName || undefined,
      page_number: pageNumber,
      page_size: pageSize,
    },
  });
  return { folders: data.folders || [], count: data.count ?? 0 };
};

/**
 * Flatten the folder tree for pickers (Move dialog). `listFolders` only returns
 * one level, so this walks breadth-first, bounded on both depth and total nodes
 * so a deep or wide tree can't fan out into hundreds of requests.
 */
export const listFolderTree = async ({ maxDepth = 4, maxNodes = 200 } = {}) => {
  const out = [];
  let frontier = [{ id: null, depth: 0 }];

  for (let depth = 0; depth < maxDepth && frontier.length && out.length < maxNodes; depth += 1) {
    const levels = await Promise.all(
      frontier.map((node) =>
        listFolders({ parentFolderId: node.id, pageSize: 100 })
          .then((r) => r.folders.map((f) => ({ ...f, depth })))
          .catch(() => [])
      )
    );
    const found = levels.flat();
    out.push(...found.slice(0, maxNodes - out.length));
    frontier = found.map((f) => ({ id: f.id, depth: depth + 1 }));
  }
  return out;
};

export const getFolder = async (id) => {
  const { data } = await client.get(`/drive/folders/${id}`);
  return { folder: data.folder, access: data.access };
};

/** `teamId` creates a team-owned root folder; nested folders inherit their parent's team. */
export const createFolder = async ({ folderName, parentFolderId = null, teamId = null }) => {
  const { data } = await client.post("/drive/folders", {
    folder_name: folderName,
    parent_folder_id: parentFolderId,
    team_id: teamId,
  });
  return data.folder;
};

export const updateFolder = async (id, { folderName, parentFolderId, isActive } = {}) => {
  const { data } = await client.patch(`/drive/folders/${id}`, {
    folder_name: folderName,
    parent_folder_id: parentFolderId,
    is_active: isActive,
  });
  return data.folder;
};

export const deleteFolder = async (id) => {
  const { data } = await client.delete(`/drive/folders/${id}`);
  return data.folder;
};

/* -------------------------------------------------------------------------- */
/* Files                                                                       */
/* -------------------------------------------------------------------------- */

export const listFiles = async ({ folderId = null, fileName, pageNumber = 1, pageSize = 100 } = {}) => {
  const { data } = await client.get("/drive/files", {
    params: {
      folder_id: folderId ?? undefined,
      file_name: fileName || undefined,
      page_number: pageNumber,
      page_size: pageSize,
    },
  });
  return { files: data.files || [], count: data.count ?? 0 };
};

/** Most recently touched files across every folder (flat, not folder-scoped). */
export const listRecentFiles = async ({ pageSize = 20 } = {}) => {
  const { data } = await client.get("/drive/files/recent", { params: { page_size: pageSize } });
  return data.files || [];
};

export const getFile = async (id) => {
  const { data } = await client.get(`/drive/files/${id}`);
  return { file: data.file, access: data.access };
};

export const updateFile = async (id, { fileName, folderId, isActive } = {}) => {
  const { data } = await client.patch(`/drive/files/${id}`, {
    file_name: fileName,
    folder_id: folderId,
    is_active: isActive,
  });
  return data.file;
};

export const deleteFile = async (id) => {
  const { data } = await client.delete(`/drive/files/${id}`);
  return data.file;
};

/**
 * `folderId: null` moves to root. The distinction matters: an omitted field
 * leaves the file where it is, while an explicit null clears the folder —
 * which is why these helpers pass the key rather than spreading an options bag.
 */
export const moveFile = async (id, folderId) => {
  const { data } = await client.patch(`/drive/files/${id}`, { folder_id: folderId });
  return data.file;
};

export const moveFolder = async (id, parentFolderId) => {
  const { data } = await client.patch(`/drive/folders/${id}`, {
    parent_folder_id: parentFolderId,
  });
  return data.folder;
};

export const getDownloadUrl = async (id) => {
  const { data } = await client.get(`/drive/files/${id}/download-url`);
  return normalizeDownloadTarget(data);
};

/** Same signed URL, logged as "previewed" rather than "downloaded" server-side. */
export const getPreviewUrl = async (id) => {
  const { data } = await client.get(`/drive/files/${id}/preview-url`);
  return normalizeDownloadTarget(data);
};

/* -------------------------------------------------------------------------- */
/* Version history                                                            */
/* -------------------------------------------------------------------------- */

export const listFileVersions = async (id) => {
  const { data } = await client.get(`/drive/files/${id}/versions`);
  return data.versions || [];
};

export const getVersionDownloadUrl = async (id, versionId) => {
  const { data } = await client.get(`/drive/files/${id}/versions/${versionId}/download-url`);
  return data.download_url;
};

export const restoreFileVersion = async (id, versionId) => {
  const { data } = await client.post(`/drive/files/${id}/versions/${versionId}/restore`);
  return data.file;
};

/** Same three-phase shape as uploadFile, but replaces an existing file's content. */
export const uploadFileVersion = async (id, file, { onProgress, signal } = {}) => {
  const { data } = await client.post(`/drive/files/${id}/versions/upload-url`, null, {
    params: { content_type: file.type || "application/octet-stream" },
  });

  await axios.put(data.upload_url, file, {
    headers: { "Content-Type": file.type || "application/octet-stream" },
    signal,
    onUploadProgress: (event) => {
      if (onProgress && event.total) onProgress(Math.round((event.loaded / event.total) * 100));
    },
  });

  const { data: confirmed } = await client.post(`/drive/files/${id}/versions/confirm`, null, {
    params: { s3_key: data.s3_key, content_type: file.type || "application/octet-stream" },
  });
  return confirmed.file;
};

/**
 * Three-phase upload: reserve a row and get a signed URL, PUT the bytes straight
 * at the storage provider (never through the API), then confirm so the server
 * can verify the object landed and flip status pending -> active.
 *
 * An unconfirmed row stays `pending` and is invisible to `listFiles`, so an
 * abandoned upload leaves no user-visible mess.
 */
export const uploadFile = async (file, { folderId = null, onProgress, signal } = {}) => {
  const { data } = await client.post("/drive/files/upload-url", {
    file_name: file.name,
    content_type: file.type || "application/octet-stream",
    folder_id: folderId,
  });
  const target = normalizeUploadTarget(data);

  try {
    // Bare axios, not `client`: the signed URL points at the storage provider,
    // which must not receive our session cookie or API baseURL.
    await axios.request({
      url: target.uploadUrl,
      method: target.method,
      data: file,
      signal,
      headers: target.headers || {
        "Content-Type": file.type || "application/octet-stream",
      },
      onUploadProgress: (event) => {
        if (onProgress && event.total) {
          onProgress(Math.round((event.loaded * 100) / event.total));
        }
      },
    });
  } catch (error) {
    if (axios.isCancel(error) || error.name === "CanceledError") throw error;
    throw new ApiError("Upload to storage failed. Please try again.", {
      code: "StorageUploadFailed",
    });
  }

  const { data: confirmed } = await client.post(`/drive/files/${target.fileId}/confirm`);
  return confirmed.file;
};

/* -------------------------------------------------------------------------- */
/* Comments                                                                    */
/* -------------------------------------------------------------------------- */

export const listComments = async (fileId) => {
  const { data } = await client.get(`/drive/files/${fileId}/comments`);
  return data.comments || [];
};

export const addComment = async (fileId, body) => {
  const { data } = await client.post(`/drive/files/${fileId}/comments`, null, { params: { body } });
  return data.comment;
};

export const deleteComment = async (fileId, commentId) => {
  await client.delete(`/drive/files/${fileId}/comments/${commentId}`);
};

/* -------------------------------------------------------------------------- */
/* Labels                                                                      */
/* -------------------------------------------------------------------------- */

export const listOrgLabels = async () => {
  const { data } = await client.get("/drive/labels");
  return data.labels || [];
};

export const createLabel = async (name, color) => {
  const { data } = await client.post("/drive/labels", null, { params: { name, color } });
  return data.label;
};

export const deleteLabel = async (labelId) => {
  await client.delete(`/drive/labels/${labelId}`);
};

export const listFileLabels = async (fileId) => {
  const { data } = await client.get(`/drive/files/${fileId}/labels`);
  return data.labels || [];
};

export const assignLabel = async (fileId, labelId) => {
  await client.post(`/drive/files/${fileId}/labels/${labelId}`);
};

export const unassignLabel = async (fileId, labelId) => {
  await client.delete(`/drive/files/${fileId}/labels/${labelId}`);
};

/* -------------------------------------------------------------------------- */
/* Search                                                                      */
/* -------------------------------------------------------------------------- */

export const search = async (q) => {
  const { data } = await client.get("/drive/search", { params: { q } });
  return { folders: data.folders || [], files: data.files || [] };
};

/* -------------------------------------------------------------------------- */
/* Shares                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * `organisationId` is the caller's own org, resolved server-side from the users
 * table — the client never has to trust its own token claim to offer
 * "share with my organisation". Null when the account has no org.
 */
export const listShares = async ({ pageNumber = 1, pageSize = 50 } = {}) => {
  const { data } = await client.get("/drive/shares", {
    params: { page_number: pageNumber, page_size: pageSize },
  });
  return {
    owned: data.owned || [],
    sharedWithMe: data.shared_with_me || [],
    organisationId: data.organisation_id ?? null,
  };
};

/**
 * Exactly one target: a public link (`isPublic`, read-only — the API rejects
 * public write), a specific user, or an organisation. The API rejects requests
 * that set more than one, and only lets you share into your own org.
 */
export const createShare = async ({
  resourceType,
  resourceId,
  isPublic = false,
  sharedWithUserId = null,
  sharedWithOrgId = null,
  permission = "read",
  expiresAt = null,
}) => {
  const { data } = await client.post("/drive/shares", {
    resource_type: resourceType,
    resource_id: resourceId,
    is_public: isPublic,
    shared_with_user_id: sharedWithUserId,
    shared_with_org_id: sharedWithOrgId,
    permission,
    expires_at: expiresAt,
  });
  return { share: data.share, publicPath: data.public_path };
};

/** Resolve a person to share with. The accounts endpoint takes an id or email. */
export const findUser = async (identifier) => {
  const { data } = await client.get(`/get_user/${encodeURIComponent(identifier)}`);
  return data.user ?? data;
};

export const deleteShare = async (id) => {
  const { data } = await client.delete(`/drive/shares/${id}`);
  return data.share;
};

/** Unauthenticated — resolves a public share token to its file or folder. */
export const resolvePublicLink = async (token) => {
  const { data } = await client.get(`/drive/public/${token}`);
  return data;
};
